import BackButton from "components/BackButton";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useVowGameContext } from "VowGameContext";
import { useWebSocket } from "WebSocketContext";

import {
  ROLE_ACTIONS,
  getActLabel,
  getPhaseTimerStatus,
  type ActionSelection,
  type Phase,
  type PhaseTimerStatus,
  type WaterManagement,
} from "./vowGameLogic";
import { formatCountdown } from "./utils/time";
import GameBoardSection from "./components/GameBoardSection";
import PlayerDashboardsSection from "./components/PlayerDashboardsSection";
import PostGameAnalysisSection from "./components/PostGameAnalysisSection";
import HostAdminPanel, { type HostRosterPlayer } from "./components/HostAdminPanel";
import ControlPanel from "./components/ControlPanel";
import AnalysisDashboards from "./components/AnalysisDashboards";
import EventLogSection from "./components/EventLogSection";
import WaitingScreen from "./components/WaitingScreen";
import TutorialModal from "./components/TutorialModal";
import { WATER_MANAGEMENT_LABELS } from "./components/waterManagementOptions";
import { BUTTON_PRIMARY, PAGE_BACKGROUND, PANEL_CLASS } from "./components/uiConstants";

const BADGE_BASE_CLASS =
  "inline-flex items-center border-2 border-black px-3 py-1 text-xs font-semibold uppercase tracking-wide";

const TIMER_PHASE_LABELS: Record<Phase, string> = {
  actions: "Planning",
  simulation: "Voting",
  ended: "Session",
};

const VowGame: React.FC = () => {
  const navigate = useNavigate();
  const { isConnected } = useWebSocket();
  const wasConnectedRef = useRef(isConnected);
  const hasRedirectedRef = useRef(false);
  const {
    sessionVersion,
    gameState,
    requestAction,
    finalizeActions,
    runSimulation,
    resetGame,
    changeWaterManagement,
    adjustPhaseTimer,
    updateTimerSettings,
    players,
    selfRole,
    playerId,
    isHost,
    hostId,
    gameId,
    extendGame,
    endGame,
    startGame,
  } = useVowGameContext();

  useEffect(() => {
    if (gameState && !selfRole && !isHost) {
      navigate("/vow/select-role", { replace: true });
    }
  }, [gameState, selfRole, navigate, isHost]);

  useEffect(() => {
    if (isConnected) {
      wasConnectedRef.current = true;
      hasRedirectedRef.current = false;
      return;
    }

    if (isHost) {
      return;
    }

    if (!wasConnectedRef.current || hasRedirectedRef.current) {
      return;
    }

    hasRedirectedRef.current = true;
    navigate("/vow/play", { replace: true });
  }, [isConnected, isHost, navigate]);

  const [isTutorialOpen, setTutorialOpen] = useState<boolean>(false);
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (!gameState) return;
    const interval = window.setInterval(() => {
      setNow(Date.now());
    }, 1000);
    return () => window.clearInterval(interval);
  }, [gameState]);

  const actionOptionsForRole = useMemo(
    () => (selfRole ? ROLE_ACTIONS[selfRole] ?? [] : []),
    [selfRole]
  );
  const [selectedAction, setSelectedAction] = useState<ActionSelection | null>(null);

  useEffect(() => {
    if (actionOptionsForRole.length > 0) {
      setSelectedAction((current) =>
        current && actionOptionsForRole.some((option) => option.value === current)
          ? current
          : actionOptionsForRole[0].value
      );
    } else {
      setSelectedAction(null);
    }
  }, [actionOptionsForRole]);

  const selfPlayer = useMemo(
    () => players.find((player) => player.id === playerId),
    [players, playerId]
  );
  const roleLocked = Boolean(selfPlayer?.roleLocked);
  const phaseTimer = gameState?.phaseTimer ?? null;
  const timerStatus = useMemo<PhaseTimerStatus | null>(() => {
    if (!phaseTimer) return null;
    return getPhaseTimerStatus(phaseTimer, now);
  }, [phaseTimer, now]);
  const timerPhaseLabel = timerStatus
    ? TIMER_PHASE_LABELS[timerStatus.phase] ?? "Timer"
    : null;
  const isTimerExpired = timerStatus?.hasExpired ?? false;
  const timerSummary = useMemo(() => {
    if (!timerStatus) {
      return { text: "—", tone: "text-slate-900" };
    }
    if (timerStatus.phase === "ended") {
      return {
        text: `${TIMER_PHASE_LABELS.ended} — Completed`,
        tone: "text-slate-900",
      };
    }
    if (timerStatus.hasExpired) {
      return {
        text: `${timerPhaseLabel ?? "Timer"} — Expired`,
        tone: "text-rose-700",
      };
    }
    const display = formatCountdown(timerStatus.remainingMs);
    const tone =
      timerStatus.remainingMs <= 30_000 ? "text-amber-700" : "text-slate-900";
    return {
      text: `${timerPhaseLabel ?? "Timer"} — ${display}`,
      tone,
    };
  }, [timerPhaseLabel, timerStatus]);

  const canPerformActions =
    Boolean(selfRole) &&
    gameState?.phase === "actions" &&
    !gameState?.gameEnded &&
    !isTimerExpired;
  const sortedPlayers = useMemo<HostRosterPlayer[]>(
    () => [...players].sort((a, b) => a.id - b.id),
    [players]
  );

  useEffect(() => {
    const prevHtmlOverflowY = document.documentElement.style.overflowY;
    const prevBodyOverflowY = document.body.style.overflowY;
    document.documentElement.style.overflowY = "auto";
    document.body.style.overflowY = "auto";
    return () => {
      document.documentElement.style.overflowY = prevHtmlOverflowY;
      document.body.style.overflowY = prevBodyOverflowY;
    };
  }, []);

  const currentActionOption = useMemo(() => {
    if (!selectedAction) return null;
    return (
      actionOptionsForRole.find((option) => option.value === selectedAction) ?? null
    );
  }, [actionOptionsForRole, selectedAction]);

  const handleFinalizeActions = () => {
    finalizeActions();
  };

  const handleRunSimulation = () => {
    runSimulation();
  };

  const handleResetGame = () => {
    if (window.confirm("Are you sure you want to reset the game?")) {
      resetGame();
    }
  };

  const handleWmChoiceChange = (value: WaterManagement) => {
    changeWaterManagement(value);
  };

  const handleBoardSubmit = useCallback(
    (action: ActionSelection, tiles: number[]) => {
      if (!gameState || gameState.phase !== "actions") return;
      if (!canPerformActions) return;
      if (tiles.length === 0) return;
      requestAction(action, tiles);
    },
    [canPerformActions, gameState, requestAction]
  );

  const buildExportPayload = useCallback(() => {
    if (!gameState) return null;
    return {
      kind: "vow-game-export",
      version: 1,
      exportedAt: new Date().toISOString(),
      metadata: {
        gameId,
        hostId,
        sessionVersion,
      },
      roster: sortedPlayers,
      state: gameState,
    };
  }, [gameId, gameState, hostId, sessionVersion, sortedPlayers]);

  const handleDownloadGameData = useCallback(() => {
    const exportPayload = buildExportPayload();
    if (!exportPayload) return;

    const blob = new Blob([JSON.stringify(exportPayload, null, 2)], {
      type: "application/json",
    });

    const downloadName = `vow-game-${gameId || "session"}-${sessionVersion || "data"}.json`;
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = downloadName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(link.href);
  }, [buildExportPayload, gameId, sessionVersion]);

  const handleNavigateToAnalysis = useCallback(() => {
    const exportPayload = buildExportPayload();
    if (!exportPayload) return;
    navigate("/vow/analyze", { state: { payload: exportPayload, fromGame: true } });
  }, [buildExportPayload, navigate]);

  if (!gameState) {
    return <WaitingScreen isHost={isHost} onStartGame={startGame} />;
  }

  const phaseLabel =
    gameState.phase === "actions"
      ? "Planning Actions"
      : gameState.phase === "simulation"
        ? "Simulation"
        : "Session Ended";

  const waterManagementLabel =
    WATER_MANAGEMENT_LABELS[gameState.wmChoice] ?? `Option ${gameState.wmChoice}`;

  const statusBanner = gameState.gameEnded
    ? {
        message: "The host has ended the game. Reset to begin a new session.",
        toneClass: "bg-[#f6d0ca] text-rose-900",
      }
    : gameState.reachedDefaultEnding && !gameState.gameEnded
      ? {
          message:
            "Base game complete — waiting for the host to extend or end the session.",
          toneClass: "bg-[#f9e3b7] text-amber-900",
        }
      : null;

  const lastHistory = gameState.history[gameState.history.length - 1];
  const previousHistory =
    gameState.history.length > 1
      ? gameState.history[gameState.history.length - 2]
      : undefined;

  const boardColumnClass = [
    "flex w-full flex-col gap-6",
    isHost
      ? "col-span-1 sm:col-span-2 lg:col-span-2 xl:col-span-2"
      : "col-span-1 sm:col-span-2 lg:col-span-2 xl:col-span-3",
  ].join(" ");

  const sideColumnClass = [
    "flex w-full flex-col gap-6",
    isHost ? "sm:col-span-2 lg:col-span-1 xl:col-span-2" : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div className={`${PAGE_BACKGROUND} text-slate-900 w-full min-h-screen`}>
      <main className="w-full">
        <div className="w-full px-4 py-6">
          <header className={`${PANEL_CLASS} w-full px-6 py-6 mb-4`}>
            <div className="flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between">
              <div className="space-y-4 text-left lg:max-w-3xl">
                <div className="flex flex-wrap items-center gap-2">
                  <span className={`${BADGE_BASE_CLASS} bg-[#f4f0e0] text-slate-900`}>
                    VOW Commons Session
                  </span>
                  {gameState.hasExtendedGame && (
                    <span className={`${BADGE_BASE_CLASS} bg-[#d9e8f5] text-slate-900`}>
                      Extended Game
                    </span>
                  )}
                  {gameState.reachedDefaultEnding && !gameState.gameEnded && (
                    <span className={`${BADGE_BASE_CLASS} bg-[#f9e3b7] text-slate-900`}>
                      Awaiting Host Decision
                    </span>
                  )}
                  {gameState.gameEnded && (
                    <span className={`${BADGE_BASE_CLASS} bg-[#f6d0ca] text-slate-900`}>
                      Session Ended
                    </span>
                  )}
                </div>
                <div className="space-y-2">
                  <h1 className="text-xl font-bold text-slate-900">
                    VOW — Voices of Wetland Commons
                  </h1>
                  <p className="text-sm text-slate-600">
                    Need a printable reference?{" "}
                    <a
                      className="font-semibold text-slate-800 underline"
                      href="/vow-commons-rpg-rules.pdf"
                      target="_blank"
                      rel="noreferrer"
                    >
                      Download the simplified game guide (PDF)
                    </a>
                    .
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setTutorialOpen(true)}
                  className={`text-sm ${BUTTON_PRIMARY} w-full sm:w-auto`}
                >
                  How to Play
                </button>
                {statusBanner && (
                  <div
                    className={`border-2 border-black px-3 py-2 text-sm font-semibold ${statusBanner.toneClass}`}
                  >
                    {statusBanner.message}
                  </div>
                )}
              </div>

              <div className="flex w-full flex-col gap-4 sm:flex-row sm:items-end sm:justify-end lg:w-auto lg:flex-nowrap">
                <dl className="grid w-full grid-cols-[max-content,1fr] gap-x-6 gap-y-3 border-2 border-black bg-[#f4f0e0] px-4 py-3 text-left text-sm text-slate-600 sm:w-auto sm:text-right">
                  <dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                    Current Act
                  </dt>
                  <dd className="text-base font-semibold text-slate-900" id="annee-actuelle">
                    {getActLabel(gameState.annee)}
                  </dd>
                  <dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                    Game Phase
                  </dt>
                  <dd className="text-base font-semibold text-slate-900">{phaseLabel}</dd>
                  <dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                    Phase Timer
                  </dt>
                  <dd className={`text-base font-semibold ${timerSummary.tone}`}>
                    {timerSummary.text}
                  </dd>
                  <dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                    Water Strategy
                  </dt>
                  <dd className="text-base font-semibold text-slate-900">
                    {waterManagementLabel}
                  </dd>
                </dl>

              </div>
            </div>
          </header>

          <div className="grid gap-4 [grid-auto-flow:dense] grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 w-full">
            <div className={boardColumnClass}>
              <GameBoardSection
                gameState={gameState}
                timerStatus={timerStatus}
                canPerformActions={canPerformActions}
                showScoreSummary={!isHost}
                showActionDetails={!isHost && Boolean(currentActionOption)}
                lastWmChoice={lastHistory?.wmChoice ?? null}
                selfRole={isHost ? null : selfRole}
                lastHistory={lastHistory}
                previousHistory={previousHistory}
                actionContext={
                  !isHost && currentActionOption
                    ? {
                        action: currentActionOption.value,
                        playerKey: currentActionOption.playerKey,
                        label: currentActionOption.label,
                      }
                    : null
                }
                onSubmitAction={handleBoardSubmit}
              />

              {isHost && (
                <PlayerDashboardsSection
                  gameState={gameState}
                  lastHistory={lastHistory}
                  previousHistory={previousHistory}
                />
              )}

              <PostGameAnalysisSection gameState={gameState} />
            </div>

            <div className={sideColumnClass}>
              {isHost ? (
                <HostAdminPanel
                  gameState={gameState}
                  timerStatus={timerStatus}
                  timerSettings={gameState.timerSettings}
                  hostId={hostId}
                  gameId={gameId}
                  sortedPlayers={sortedPlayers}
                  onFinalizeActions={handleFinalizeActions}
                  onRunSimulation={handleRunSimulation}
                  onResetGame={handleResetGame}
                  onWaterManagementChange={handleWmChoiceChange}
                  onAdjustTimer={adjustPhaseTimer}
                  onUpdateTimerSettings={updateTimerSettings}
                  onExtendGame={extendGame}
                  onEndGame={endGame}
                  onDownloadGameData={handleDownloadGameData}
                  onViewAnalysis={handleNavigateToAnalysis}
                />
              ) : (
              <ControlPanel
                gameState={gameState}
                timerStatus={timerStatus}
                playerId={playerId}
                selfRole={selfRole}
                roleLocked={roleLocked}
                onWaterManagementChange={handleWmChoiceChange}
                onSelectRole={() => navigate("/vow/select-role")}
                selfPlayerName={selfPlayer?.playerName}
                />
              )}

              <EventLogSection entries={gameState.log} />
            </div>

            <AnalysisDashboards gameState={gameState} />
          </div>
        </div>
      </main>

      <footer className="w-full">
        <div className="fixed bottom-4 left-4 z-50">
          {/*<BackButton />*/}
        </div>
      </footer>
      <TutorialModal isOpen={isTutorialOpen} onClose={() => setTutorialOpen(false)} />
    </div>
  );
};

export default VowGame;
